import { defineStore } from 'pinia';
import { reactive, computed, toRefs } from 'vue';
import type { SelectedItem } from './useCollectionSelectionStore';
import { post } from '~/utils/api';
import type { BagMeta } from '~/modules/add-to-bag/bag';
export interface AddToBagLineItemRequest {
  upcId: string;
  upcNumber: string;
  registryId: number;
  source: string;
  loyaltyPoints: boolean;
  quantity: number;
  pickUpFromStore: string;
  pdpAtbProtectionPlanEligible: boolean;
  isMarketPlaceEligibleFlag: boolean;
  sellerId: number | null;
  autoreplenishment: null;
}

export interface MultiAddToBagRequestPayload {
  lineItems: AddToBagLineItemRequest[];
}

export interface BagApiError {
  errorCode: string;
  errorMessage: string;
  displayMessage: string;
}

export interface BagLineItemResult {
  upcId: number;
  upcNumber: number;
  productId: number;
  status: 'ADDED' | 'FAILED';
  sequenceNumber: number | null;
  quantity: number;
  retailPrice: number | null;
  originalPrice: number | null;
  itemTotal: number | null;
  bigTicketItem: boolean;
  bigTicketDeliveryFeeDisclaimer: string | null;
  errors: BagApiError[];
}

export interface BagSummary {
  itemCount: number;
  preTaxOrderTotal: number;
  merchandiseTotal: number;
  merchandiseTotalBeforeTotalSaved: number;
  totalSaved: number;
  grandTotal: number;
  estimatedTax: number;
  estimatedDeliveryFee: number;
  freeShipping: {
    thresholdAmount: number;
    amountRemainingToThreshold: number;
    freeShippingEligible: boolean;
  };
}

export interface BagApiResponse {
  status: 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILURE';
  addedItemsCount: number;
  totalItemsRequested: number;
  bagSummary: BagSummary | null;
  lineItemResults: BagLineItemResult[];
  errors: BagApiError[];
  meta: BagMeta;
  bag_Guid?: string;
  bagId?: string;
  userId?: string;
  userGuid?: string;
}

// Maps v2 flat response structure to the nested v1 format expected by setCookiesFromResponse
const mapV2ToV1Response = (v2Response: BagApiResponse) => ({
  bag: {
    meta: {
      userId: v2Response.userId,
      userGuid: v2Response.userGuid,
      bagGuid: v2Response.bag_Guid,
      bagAttributes: {
        bagItemQuantity: v2Response.bagSummary?.itemCount,
      },
    },
  },
});

const getDefaultState = () => ({
  failedUpcs: {} as Record<string, string[]>,
  globalErrors: [] as string[],
  isLoading: false,
  lastBagSummary: null as BagSummary | null,
  lastAddedItems: [] as BagLineItemResult[],
  lastSubmittedItems: [] as SelectedItem[],
  addedItemsCount: 0,
  totalItemsRequested: 0,
});

const globalErrorText = 'Something went wrong. Please try again.';
const itemsNotAddedErrorText = "Items weren't added to your bag.";

export const useMultiATBStore = defineStore('useMultiATBStore', () => {
  const state = reactive({
    ...getDefaultState(),
    isBuyNowLoading: false,
    isAddToBagLoading: false,
  });

  /** Actual savings amount from the bag response (Order Level) */
  const savings = computed(() => state.lastBagSummary?.totalSaved || 0);

  /** Calculated percentage of savings based on the bag response (Order Level) */
  const savingsInPercents = computed(() => {
    if (!state.lastBagSummary || !state.lastBagSummary.totalSaved || !state.lastBagSummary.merchandiseTotalBeforeTotalSaved) {
      return 0;
    }
    const percent = (state.lastBagSummary.totalSaved / state.lastBagSummary.merchandiseTotalBeforeTotalSaved) * 100;
    return Math.round(percent);
  });

  const resetState = (): void => {
    Object.assign(state, getDefaultState());
  };

  const _extractLeveltemError = (lineItemResults: BagLineItemResult[]): void => {
    state.lastAddedItems = lineItemResults;
    lineItemResults.forEach((result) => {
      if (result.status === 'FAILED' && result.errors?.length) {
        state.failedUpcs[String(result.upcId)] = result.errors.map((err) => err.displayMessage).filter(Boolean);
      }
    });
  };

  const _applyResponseToState = (response: BagApiResponse): void => {
    state.addedItemsCount = response.addedItemsCount || 0;
    state.totalItemsRequested = response.totalItemsRequested || 0;

    if (response.bagSummary) {
      state.lastBagSummary = response.bagSummary;
    }

    if (response.lineItemResults) {
      _extractLeveltemError(response.lineItemResults);
    }
  };

  const _handleSuccessCookieSync = (response: BagApiResponse, setCookiesFromResponse: (data: unknown) => boolean): boolean => {
    const mappedResponse = mapV2ToV1Response(response);
    const hasValidSession = setCookiesFromResponse(mappedResponse);
    if (!hasValidSession) {
      state.globalErrors = [globalErrorText];
      return false;
    }
    return true;
  };

  const _handleFailureErrors = (response: BagApiResponse): void => {
    const hasLineResults = response.lineItemResults && response.lineItemResults.length > 0;
    const firstError = response.errors?.[0];
    const displayMessage = (!hasLineResults && firstError?.displayMessage) || itemsNotAddedErrorText;
    state.globalErrors = [displayMessage];
  };

  /**
   * Internal helper to perform the API call and update state.
   * @private
   */
  const _submit = async (
    addToBagPayload: MultiAddToBagRequestPayload,
    currentSelections: SelectedItem[],
  ): Promise<{ success: boolean; data: BagApiResponse | unknown }> => {
    // Reset previous errors and response state
    resetState();

    try {
      // Save snapshot of current items before sending request
      state.lastSubmittedItems = JSON.parse(JSON.stringify(currentSelections));
      state.isLoading = true;

      const { PersistentLoginUtil } = await import('@nuxt-common/util/app/utils/persistentLogin');
      const [response, { default: setCookiesFromResponse }] = await Promise.all([
        post<BagApiResponse>('/xapi/bag/v2/add', addToBagPayload, {
          headers: {
            'content-type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
            ...PersistentLoginUtil.getDeviceFingerPrintHeaders(),
          },
        }),
        import('@nuxt-common/add-to-bag/app/utils/setCookiesFromResponse'),
      ]);

      _applyResponseToState(response);

      const success = response.status === 'SUCCESS' || response.status === 'PARTIAL_SUCCESS';

      if (success) {
        const isSessionValid = _handleSuccessCookieSync(response, setCookiesFromResponse);
        if (!isSessionValid) {
          return { success: false, data: response };
        }
      } else {
        _handleFailureErrors(response);
      }

      return { success, data: response };
    } catch (error) {
      state.globalErrors = [globalErrorText];
      return { success: false, data: error };
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * Adds multiple items to the bag.
   */
  const addToBag = async (payload: MultiAddToBagRequestPayload, selections: SelectedItem[]) => {
    state.isAddToBagLoading = true;
    try {
      return await _submit(payload, selections);
    } finally {
      state.isAddToBagLoading = false;
    }
  };

  /**
   * Performs "Buy Now" for multiple items (Add to Bag).
   */
  const buyNow = async (
    payload: MultiAddToBagRequestPayload,
    selections: SelectedItem[],
    expressCheckoutService?: { checkout: () => Promise<void> },
    onSuccess?: (response: { success: boolean; data: BagApiResponse | unknown }) => void,
  ) => {
    state.isBuyNowLoading = true;
    try {
      const response = await _submit(payload, selections);
      if (response.success) {
        if (onSuccess) onSuccess(response);
        const isPartialSuccess = (response.data as BagApiResponse)?.status === 'PARTIAL_SUCCESS';
        if (expressCheckoutService && !isPartialSuccess) {
          await expressCheckoutService.checkout();
        }
      }
      return response;
    } catch (error) {
      state.globalErrors = [globalErrorText];
      return { success: false, data: error };
    } finally {
      state.isBuyNowLoading = false;
    }
  };

  return {
    ...toRefs(state),
    savings,
    savingsInPercents,
    addToBag,
    buyNow,
    resetState,
  };
});
